import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Loader2, DollarSign, AlertTriangle, CheckCircle, ShieldCheck } from 'lucide-react';
import { diagnosticsAPI } from '../../lib/api';

function StageBadge({ status }) {
  if (status === 'PASS') return <Badge variant="default" className="text-[10px]">PASS</Badge>;
  if (status === 'FAIL') return <Badge variant="destructive" className="text-[10px]">FAIL</Badge>;
  if (status === 'BLOCKED') return <Badge variant="outline" className="text-[10px]">BLOCKED</Badge>;
  return null;
}

function fmt(n) {
  if (n == null) return '-';
  return Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function InvoiceCurrencyDiagnosticPanel() {
  const [distribution, setDistribution] = useState(null);
  const [verdict, setVerdict] = useState(null);
  const [xeroCompare, setXeroCompare] = useState(null);
  const [recon, setRecon] = useState(null);
  const [loading, setLoading] = useState(true);
  const [xeroLoading, setXeroLoading] = useState(false);
  const [reconLoading, setReconLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      diagnosticsAPI.invoiceCurrencyDistribution(),
      diagnosticsAPI.invoiceCurrencyVerdict(),
    ])
      .then(([distRes, verdictRes]) => {
        if (cancelled) return;
        setDistribution(distRes.data);
        setVerdict(verdictRes.data);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const runXeroCompare = async () => {
    setXeroLoading(true);
    try {
      const res = await diagnosticsAPI.invoiceCurrencyXeroCompare({ sample_limit: 50 });
      setXeroCompare(res.data);
    } catch {
      setXeroCompare({ error: 'Failed to fetch Xero comparison.' });
    } finally {
      setXeroLoading(false);
    }
  };

  const runReconciliation = async () => {
    setReconLoading(true);
    try {
      const res = await diagnosticsAPI.invoiceCurrencyReconciliation({ display_currency: 'GBP', xero_sample_limit: 50 });
      setRecon(res.data);
    } catch {
      setRecon({ error: 'Failed to run reconciliation.' });
    } finally {
      setReconLoading(false);
    }
  };

  const hasConversionGap = verdict?.conversion_gap_currencies?.length > 0;
  const hasMissingCurrency = (verdict?.missing_currency_count || 0) > 0;
  const hasXeroMismatches = xeroCompare && xeroCompare.currency_mismatches > 0;
  const isOk = !hasConversionGap && !hasMissingCurrency;

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <DollarSign className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">Invoice Currency Diagnostic</CardTitle>
          {!loading && (
            <Badge
              variant={isOk ? 'default' : 'destructive'}
              className="text-[10px] ml-auto"
            >
              {isOk ? 'OK' : 'Issues Found'}
            </Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          Currency distribution, FX support coverage, Xero label comparison, and reconciliation.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : (
          <>
            {/* Verdict */}
            {verdict && (
              <div className={`p-3 rounded text-xs border ${isOk ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                <div className="flex items-start gap-2">
                  {isOk
                    ? <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                    : <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
                  }
                  <span className="font-mono whitespace-pre-wrap">{verdict.verdict}</span>
                </div>
              </div>
            )}

            {/* Currency Distribution Table */}
            {distribution && (
              <div>
                <h4 className="text-xs font-medium text-muted-foreground mb-1">
                  Currency Distribution ({distribution.total_invoices} invoices)
                </h4>
                {distribution.skipped_malformed > 0 && (
                  <p className="text-[11px] text-amber-600 mb-1" data-testid="invoice-currency-skipped-malformed">
                    {distribution.skipped_malformed} sample(s) skipped: malformed stored data, see server log.
                  </p>
                )}
                <div className="border rounded overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-3 py-1.5 font-medium">Currency</th>
                        <th className="text-right px-3 py-1.5 font-medium">Count</th>
                        <th className="text-right px-3 py-1.5 font-medium">Total Amount</th>
                        <th className="text-center px-3 py-1.5 font-medium">Backend FX</th>
                        <th className="text-center px-3 py-1.5 font-medium">Frontend FX</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(distribution.by_currency).map(([cur, info]) => {
                        const backendOk = distribution.backend_supported_currencies.includes(cur);
                        const frontendOk = distribution.frontend_supported_currencies.includes(cur);
                        return (
                          <tr key={cur} className="border-b last:border-0">
                            <td className="px-3 py-1.5 font-mono">{cur === '__missing__' ? '(missing)' : cur}</td>
                            <td className="px-3 py-1.5 text-right">{info.count.toLocaleString()}</td>
                            <td className="px-3 py-1.5 text-right font-mono">
                              {cur === '__missing__' ? '-' : `${cur} ${info.total_amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                            </td>
                            <td className="px-3 py-1.5 text-center">
                              <Badge variant={backendOk ? 'default' : 'destructive'} className="text-[10px]">
                                {backendOk ? 'yes' : 'no'}
                              </Badge>
                            </td>
                            <td className="px-3 py-1.5 text-center">
                              <Badge variant={frontendOk ? 'default' : 'destructive'} className="text-[10px]">
                                {frontendOk ? 'yes' : 'no'}
                              </Badge>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Non-standard currency samples */}
            {distribution?.non_standard_samples && Object.keys(distribution.non_standard_samples).length > 0 && (
              <div>
                <h4 className="text-xs font-medium text-muted-foreground mb-1">
                  Non-GBP/EUR Invoice Samples
                </h4>
                {Object.entries(distribution.non_standard_samples).map(([cur, samples]) => (
                  <div key={cur} className="mb-2">
                    <span className="text-xs font-mono font-medium">{cur}</span>
                    <div className="border rounded mt-1 overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b bg-muted/50">
                            <th className="text-left px-2 py-1 font-medium">Invoice #</th>
                            <th className="text-left px-2 py-1 font-medium">Supplier</th>
                            <th className="text-left px-2 py-1 font-medium">Entity</th>
                            <th className="text-left px-2 py-1 font-medium">Source</th>
                            <th className="text-right px-2 py-1 font-medium">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {samples.map((s, i) => (
                            <tr key={i} className="border-b last:border-0">
                              <td className="px-2 py-1 font-mono">{s.invoice_number || '-'}</td>
                              <td className="px-2 py-1">{s.supplier_client_name || '-'}</td>
                              <td className="px-2 py-1">{s.entity_id || '-'}</td>
                              <td className="px-2 py-1">{s.source || '-'}</td>
                              <td className="px-2 py-1 text-right font-mono">
                                {s.amount != null ? s.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Xero Comparison */}
            <div className="border-t pt-3">
              <div className="flex items-center gap-2 mb-2">
                <h4 className="text-xs font-medium text-muted-foreground">
                  Xero CurrencyCode Comparison
                </h4>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-6 text-xs"
                  onClick={runXeroCompare}
                  disabled={xeroLoading}
                >
                  {xeroLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                  {xeroCompare ? 'Re-run' : 'Run Comparison'}
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground mb-2">
                Re-reads a sample of invoices from Xero API and compares CurrencyCode and Total
                against stored values. Requires active Xero connection.
              </p>
              {xeroCompare && (
                <>
                  {xeroCompare.error ? (
                    <p className="text-xs text-red-600">{xeroCompare.error}</p>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex gap-4 text-xs">
                        <span>Checked: <strong>{xeroCompare.checked}</strong></span>
                        <span>Currency mismatches: <strong className={hasXeroMismatches ? 'text-red-600' : ''}>{xeroCompare.currency_mismatches}</strong></span>
                        <span>Amount mismatches: <strong className={xeroCompare.amount_mismatches > 0 ? 'text-red-600' : ''}>{xeroCompare.amount_mismatches}</strong></span>
                      </div>
                      {xeroCompare.currency_mismatch_summary && Object.keys(xeroCompare.currency_mismatch_summary).length > 0 && (
                        <div className="text-xs">
                          <span className="font-medium">Mismatch patterns: </span>
                          {Object.entries(xeroCompare.currency_mismatch_summary).map(([pattern, count]) => (
                            <Badge key={pattern} variant="destructive" className="text-[10px] mr-1">
                              {pattern}: {count}
                            </Badge>
                          ))}
                        </div>
                      )}
                      {xeroCompare.currency_mismatch_samples?.length > 0 && (
                        <div className="border rounded overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="border-b bg-muted/50">
                                <th className="text-left px-2 py-1 font-medium">Invoice #</th>
                                <th className="text-left px-2 py-1 font-medium">Supplier</th>
                                <th className="text-center px-2 py-1 font-medium">Stored</th>
                                <th className="text-center px-2 py-1 font-medium">Xero</th>
                                <th className="text-right px-2 py-1 font-medium">Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {xeroCompare.currency_mismatch_samples.map((m, i) => (
                                <tr key={i} className="border-b last:border-0">
                                  <td className="px-2 py-1 font-mono">{m.invoice_number || '-'}</td>
                                  <td className="px-2 py-1">{m.supplier_client_name || '-'}</td>
                                  <td className="px-2 py-1 text-center">
                                    <Badge variant="destructive" className="text-[10px]">{m.stored_currency}</Badge>
                                  </td>
                                  <td className="px-2 py-1 text-center">
                                    <Badge variant="default" className="text-[10px]">{m.xero_currency}</Badge>
                                  </td>
                                  <td className="px-2 py-1 text-right font-mono">{m.stored_amount?.toLocaleString()}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                      {xeroCompare.amount_mismatch_samples?.length > 0 && (
                        <div>
                          <h5 className="text-xs font-medium text-red-600 mb-1">Amount Mismatches (transformed, not label-only)</h5>
                          <div className="border border-red-200 rounded overflow-x-auto">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="border-b bg-red-50">
                                  <th className="text-left px-2 py-1 font-medium">Invoice #</th>
                                  <th className="text-right px-2 py-1 font-medium">Stored</th>
                                  <th className="text-right px-2 py-1 font-medium">Xero</th>
                                  <th className="text-right px-2 py-1 font-medium">Delta</th>
                                </tr>
                              </thead>
                              <tbody>
                                {xeroCompare.amount_mismatch_samples.map((m, i) => (
                                  <tr key={i} className="border-b last:border-0">
                                    <td className="px-2 py-1 font-mono">{m.invoice_number || '-'}</td>
                                    <td className="px-2 py-1 text-right font-mono">{m.stored_amount?.toLocaleString()}</td>
                                    <td className="px-2 py-1 text-right font-mono">{m.xero_total?.toLocaleString()}</td>
                                    <td className="px-2 py-1 text-right font-mono text-red-600">{m.delta?.toLocaleString()}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* ── Currency Reconciliation ──────────────────────────── */}
            <div className="border-t pt-3">
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck className="h-4 w-4 text-muted-foreground" />
                <h4 className="text-xs font-medium text-muted-foreground">
                  Currency Reconciliation
                </h4>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-6 text-xs"
                  onClick={runReconciliation}
                  disabled={reconLoading}
                >
                  {reconLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                  {recon ? 'Re-run' : 'Run Reconciliation'}
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground mb-2">
                Proves correctness: (1) stored values match Xero source, (2) per-invoice
                converted totals equal the aggregate. Read-only and idempotent.
              </p>

              {recon && (
                <>
                  {recon.error ? (
                    <p className="text-xs text-red-600">{recon.error}</p>
                  ) : (
                    <div className="space-y-3">
                      {/* Overall verdict banner */}
                      <div className={`p-3 rounded text-xs border ${
                        recon.overall_status === 'PASS' ? 'bg-green-50 border-green-200' :
                        recon.overall_status === 'BLOCKED' ? 'bg-yellow-50 border-yellow-200' :
                        'bg-red-50 border-red-200'
                      }`}>
                        <div className="flex items-start gap-2">
                          {recon.overall_status === 'PASS'
                            ? <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                            : recon.overall_status === 'BLOCKED'
                              ? <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5 shrink-0" />
                              : <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
                          }
                          <div>
                            <div className="font-medium mb-1">
                              Overall: <StageBadge status={recon.overall_status} />
                            </div>
                            {recon.verdict_lines?.map((line, i) => (
                              <div key={i} className="font-mono">{line}</div>
                            ))}
                            <div className="text-muted-foreground mt-1">
                              Timestamp: {recon.timestamp}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Stage 1: Xero Comparison */}
                      {recon.stage1_xero_comparison && (
                        <div>
                          <h5 className="text-xs font-medium mb-1 flex items-center gap-1">
                            Stage 1: Storage vs Xero
                            <StageBadge status={recon.stage1_xero_comparison.status} />
                          </h5>
                          {recon.stage1_xero_comparison.status === 'BLOCKED' ? (
                            <p className="text-xs text-yellow-700">{recon.stage1_xero_comparison.reason}</p>
                          ) : (
                            <div className="flex gap-4 text-xs">
                              <span>Checked: <strong>{recon.stage1_xero_comparison.checked}</strong></span>
                              <span>Currency mismatches: <strong className={recon.stage1_xero_comparison.currency_mismatches > 0 ? 'text-red-600' : ''}>
                                {recon.stage1_xero_comparison.currency_mismatches}
                              </strong></span>
                              <span>Total mismatches: <strong className={recon.stage1_xero_comparison.total_mismatches > 0 ? 'text-red-600' : ''}>
                                {recon.stage1_xero_comparison.total_mismatches}
                              </strong></span>
                            </div>
                          )}
                          {recon.stage1_xero_comparison.currency_mismatch_samples?.length > 0 && (
                            <div className="border rounded overflow-x-auto mt-1">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="border-b bg-muted/50">
                                    <th className="text-left px-2 py-1 font-medium">Invoice #</th>
                                    <th className="text-center px-2 py-1 font-medium">Stored</th>
                                    <th className="text-center px-2 py-1 font-medium">Xero</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {recon.stage1_xero_comparison.currency_mismatch_samples.map((m, i) => (
                                    <tr key={i} className="border-b last:border-0">
                                      <td className="px-2 py-1 font-mono">{m.invoice_number || '-'}</td>
                                      <td className="px-2 py-1 text-center">
                                        <Badge variant="destructive" className="text-[10px]">{m.stored_currency}</Badge>
                                      </td>
                                      <td className="px-2 py-1 text-center">
                                        <Badge variant="default" className="text-[10px]">{m.xero_currency}</Badge>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                          {recon.stage1_xero_comparison.total_mismatch_samples?.length > 0 && (
                            <div className="mt-1">
                              <h6 className="text-xs font-medium text-red-600 mb-1">Total Mismatches</h6>
                              <div className="border border-red-200 rounded overflow-x-auto">
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr className="border-b bg-red-50">
                                      <th className="text-left px-2 py-1 font-medium">Invoice #</th>
                                      <th className="text-right px-2 py-1 font-medium">Stored</th>
                                      <th className="text-right px-2 py-1 font-medium">Xero</th>
                                      <th className="text-right px-2 py-1 font-medium">Delta</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {recon.stage1_xero_comparison.total_mismatch_samples.map((m, i) => (
                                      <tr key={i} className="border-b last:border-0">
                                        <td className="px-2 py-1 font-mono">{m.invoice_number || '-'}</td>
                                        <td className="px-2 py-1 text-right font-mono">{m.stored_total?.toLocaleString()}</td>
                                        <td className="px-2 py-1 text-right font-mono">{m.xero_total?.toLocaleString()}</td>
                                        <td className="px-2 py-1 text-right font-mono text-red-600">{m.delta?.toLocaleString()}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Stage 2: Aggregate Reconciliation */}
                      {recon.stage2_aggregate_reconciliation && (() => {
                        const s2 = recon.stage2_aggregate_reconciliation;
                        return (
                          <div>
                            <h5 className="text-xs font-medium mb-1 flex items-center gap-1">
                              Stage 2: Aggregate vs Converted-Native ({s2.period})
                              <StageBadge status={s2.status} />
                            </h5>
                            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs mb-2">
                              <span className="text-muted-foreground">Period:</span>
                              <span className="font-mono">{s2.period} ({s2.invoice_count} invoices)</span>
                              <span className="text-muted-foreground">Per-invoice converted:</span>
                              <span className="font-mono">{s2.display_currency} {fmt(s2.per_invoice_converted_total)}</span>
                              <span className="text-muted-foreground">Displayed path total:</span>
                              <span className="font-mono">{s2.display_currency} {fmt(s2.displayed_path_total)}</span>
                              <span className="text-muted-foreground">Face-value aggregate:</span>
                              <span className={`font-mono ${s2.face_value_bug_detected ? 'text-red-600 font-medium' : ''}`}>
                                {s2.display_currency} {fmt(s2.face_value_aggregate_total)}
                                {s2.face_value_bug_detected ? ' (BUG)' : ''}
                              </span>
                              <span className="text-muted-foreground">Drift (vs displayed):</span>
                              <span className={`font-mono ${s2.status === 'FAIL' ? 'text-red-600' : ''}`}>
                                {s2.display_currency} {fmt(s2.drift)} (tolerance: {fmt(s2.rounding_tolerance)})
                              </span>
                              <span className="text-muted-foreground">Bucket-rate divergence (info):</span>
                              <span className="font-mono text-muted-foreground">
                                {s2.display_currency} {fmt(s2.bucket_drift)}
                              </span>
                              {s2.unconvertible_count > 0 && (
                                <>
                                  <span className="text-muted-foreground">Unconvertible:</span>
                                  <span className="text-yellow-700">{s2.unconvertible_count} ({s2.unconvertible_currencies?.join(', ')})</span>
                                </>
                              )}
                            </div>

                            {/* Currency breakdown table */}
                            {s2.currency_breakdown?.length > 0 && (
                              <div className="border rounded overflow-x-auto">
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr className="border-b bg-muted/50">
                                      <th className="text-left px-2 py-1 font-medium">Currency</th>
                                      <th className="text-right px-2 py-1 font-medium">Native Total</th>
                                      <th className="text-right px-2 py-1 font-medium">Rate</th>
                                      <th className="text-right px-2 py-1 font-medium">Converted ({s2.display_currency})</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {s2.currency_breakdown.map((row, i) => (
                                      <tr key={i} className="border-b last:border-0">
                                        <td className="px-2 py-1 font-mono">{row.currency}</td>
                                        <td className="px-2 py-1 text-right font-mono">{row.currency} {fmt(row.native_total)}</td>
                                        <td className="px-2 py-1 text-right font-mono text-muted-foreground">{row.rate_to_display}</td>
                                        <td className="px-2 py-1 text-right font-mono">{s2.display_currency} {fmt(row.converted_total)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {/* Stage 3: Context (FX coverage) */}
                      {recon.stage3_context && (
                        <div>
                          <h5 className="text-xs font-medium mb-1">FX Coverage</h5>
                          <div className="flex flex-wrap gap-1">
                            {Object.entries(recon.stage3_context.fx_coverage || {}).map(([cur, info]) => (
                              <Badge
                                key={cur}
                                variant={info.supported ? 'default' : 'destructive'}
                                className="text-[10px]"
                              >
                                {cur}: {info.note}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
